import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, BadgeCheck, MapPin, Star } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import type { ServiceDetail as ServiceDetailType } from 'shared'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/skeleton'
import { ThemeToggle } from '../../components/ThemeToggle'
import { useService } from '../../hooks/useService'
import { ApiError } from '../../lib/api'
import { categoryLabel } from 'shared'

const ServiceDetail = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data, isLoading, isError, error } = useService(id)

  const isNotFound = error instanceof ApiError && error.status === 404

  // step back through in-app history when we have some, else fall back to the hub
  const goBack = () => {
    if (window.history.state?.idx > 0) navigate(-1)
    else navigate('/enter')
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="sticky top-0 z-10 bg-background/70 backdrop-blur supports-[backdrop-filter]:bg-background/50 border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={goBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="size-4" />
            Back
          </button>
          <div className="flex-1" />
          <ThemeToggle />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-56 w-full rounded-2xl" />
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : isNotFound ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-lg font-semibold">Service not found</p>
            <p className="text-muted-foreground">This service may have been removed.</p>
            <Button render={<Link to="/customer" />}>Browse services</Button>
          </div>
        ) : isError || !data ? (
          <div className="py-16 text-center text-muted-foreground">
            <p>Couldn’t load this service.</p>
          </div>
        ) : (
          <ServiceDetailContent service={data.service} />
        )}
      </div>
    </div>
  )
}

const ServiceDetailContent = ({ service }: { service: ServiceDetailType }) => {
  const { provider } = service

  return (
    <div className="space-y-6">
      {service.imageUrl ? (
        <img src={service.imageUrl} alt={service.title} className="h-56 w-full object-cover rounded-2xl" />
      ) : (
        <div className="h-56 w-full bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl" />
      )}

      <div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">{service.title}</h1>
            <p className="text-muted-foreground">{categoryLabel(service.category)}</p>
          </div>
          <div className="text-right shrink-0">
            <div className="flex items-center justify-end gap-1 text-amber-500">
              <Star className="size-4 fill-current" />
              <span className="text-foreground font-medium">{service.rating.toFixed(1)}</span>
            </div>
            <p className="text-sm text-muted-foreground">{service.reviews} reviews</p>
          </div>
        </div>
        <p className="mt-2 font-medium">From ${service.priceFrom}</p>
      </div>

      {service.description && <p className="text-muted-foreground">{service.description}</p>}

      {service.tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {service.tags.map((tag) => (
            <Badge key={tag} variant="secondary">
              {tag}
            </Badge>
          ))}
        </div>
      )}

      <Card>
        <CardContent className="space-y-2">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold">{provider.name}</h2>
            {provider.verified && (
              <Badge variant="secondary">
                <BadgeCheck className="size-3" />
                Verified
              </Badge>
            )}
          </div>
          {provider.bio && <p className="text-sm text-muted-foreground">{provider.bio}</p>}
          {provider.location && (
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <MapPin className="size-4" />
              <span>
                {provider.location.city}, {provider.location.region}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      <div>
        <h2 className="font-semibold mb-3">Reviews ({service.reviewList.length})</h2>
        {service.reviewList.length === 0 ? (
          <p className="text-sm text-muted-foreground">No reviews yet.</p>
        ) : (
          <div className="space-y-3">
            {service.reviewList.map((review) => (
              <Card key={review.id}>
                <CardContent className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{review.userName}</span>
                    <div className="flex items-center gap-1 text-amber-500">
                      <Star className="size-3.5 fill-current" />
                      <span className="text-foreground text-sm">{review.rating}</span>
                    </div>
                  </div>
                  {review.comment && <p className="text-sm text-muted-foreground">{review.comment}</p>}
                  <p className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(review.createdAt), { addSuffix: true })}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default ServiceDetail
